import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ArrowRight, AudioRecorder, Button, WordScore } from '@astrid/components'
import ScoreIndicator from 'components/ScoreIndicator/ScoreIndicator'
import useEnhancedState from 'hooks/useEnhancedState'
import usePlayer from 'hooks/usePlayer'
import { Syllable, WordPracticeEmphasis } from 'store/services/Lesson'
import { getTextToSpeechLink } from 'store/services/Exercise'
import { selectEnglishDialect } from 'store/services/User'

import { getScoreColor, getWordSize } from '../helpers'
import AssistControls from '../AssistControls/AssistControls'
import styles from './WordsPracticeReading.module.scss'

type WordsPracticeReadingProps = {
  attempts: number
  word: string
  wordScore: WordScore | null
  index?: number
  numberOfWords?: number
  predefinedSyllables?: Syllable[]
  isSuccess: boolean
  isCreator?: boolean
  isGeneratingScore: boolean
  emphasis?: WordPracticeEmphasis
  getScore: (path: Blob) => Promise<void>
  goToNextWord: () => void
  onPlayPauseAudio?: (disruptReason?: string) => void
}

interface ExercisePractiseReadingState {
  initialAssistantFinished: boolean
  recordingPath: Blob | null
  isAssistantPlaying: boolean
  isRecordingPlaying: boolean
  isRecording: boolean
}

const WordsPracticeReading: React.FunctionComponent<WordsPracticeReadingProps> = ({
  word,
  wordScore,
  attempts,
  index,
  isSuccess,
  isCreator,
  numberOfWords,
  emphasis,
  goToNextWord,
  getScore,
  isGeneratingScore,
  predefinedSyllables,
  onPlayPauseAudio
}) => {
  const englishDialect = useSelector(selectEnglishDialect)
  const assistantPlayer = usePlayer(true)
  const [state, setState] = useEnhancedState<ExercisePractiseReadingState>({
    initialAssistantFinished: false,
    recordingPath: null,
    isAssistantPlaying: false,
    isRecordingPlaying: false,
    isRecording: false
  })

  const scoreIndicatorSize = Math.min(window.innerWidth * 0.5, window.innerHeight * 0.25)

  const startAssistant = async () => {
    await assistantPlayer.stopSound()

    setState({ isAssistantPlaying: true, isRecordingPlaying: false })

    await assistantPlayer.playSound({
      path: getTextToSpeechLink(word, englishDialect),
      onFinish: () => {
        setState({ initialAssistantFinished: true, isAssistantPlaying: false })
      }
    })
    onPlayPauseAudio?.()
  }

  const replayRecording = async () => {
    await assistantPlayer.stopSound()

    if (state.recordingPath) {
      setState({ isRecordingPlaying: true, isAssistantPlaying: false })

      assistantPlayer.playSound({
        path: state.recordingPath,
        onFinish: () => {
          setState({ isRecordingPlaying: false })
        }
      })

      onPlayPauseAudio?.()
    }
  }

  const stopRecording = async (path: Blob) => {
    try {
      setState({ recordingPath: path, isRecording: false })
      await getScore(path)
    } catch (error) {}
  }

  const startRecording = async () => {
    await assistantPlayer.stopSound()

    setState({ isAssistantPlaying: false, isRecordingPlaying: false, isRecording: true })
  }

  const wordStyle = useMemo(() => getWordSize(word.length), [word])

  return (
    <div className={styles.container}>
      {index !== undefined && numberOfWords ? (
        <div className={styles.index}>
          {index + 1}/{numberOfWords}
        </div>
      ) : null}
      <div className={styles.top}>
        {wordScore ? (
          <ScoreIndicator
            value={wordScore.score}
            sizeInPx={scoreIndicatorSize}
            strokeWidthInPx={scoreIndicatorSize / 5}
            fontSize="48px"
            colorsThresholds={{
              LOW: 50,
              HIGH: 80
            }}
          />
        ) : null}
      </div>
      <div className={styles.bottom}>
        <div className={styles.word} style={{ ...wordStyle, color: getScoreColor(wordScore?.score) }}>
          {word}
        </div>
        <div className={styles.syllables}>
          {wordScore?.syllable_scores?.map((s, i) => (
            <React.Fragment key={i}>
              <span style={{ color: getScoreColor(s.score) }}>{s.syllable_ipa}</span>

              {i < wordScore.syllable_scores.length - 1 && ' - '}
            </React.Fragment>
          ))}
          {!wordScore &&
            predefinedSyllables?.map((s, i) => (
              <React.Fragment key={i}>
                {s.phonemesIpa.map((p) => (
                  <span key={p} className={p === emphasis?.phonemeIpa ? styles.underlined : ''}>
                    {p}
                  </span>
                ))}

                {i < predefinedSyllables.length - 1 && ' - '}
              </React.Fragment>
            ))}
        </div>
        <div className={styles.spacer} />
        <AssistControls
          disableControls={state.isRecording}
          isPlayingAssistant={state.isAssistantPlaying}
          isPlayingRecording={state.isRecordingPlaying}
          onPlayAssistant={startAssistant}
          onPlayRecording={replayRecording}
          recordingAvailable={!!state.recordingPath}
        />
        <div className={styles.actions}>
          <AudioRecorder onStopRecording={stopRecording} onStartRecording={startRecording} />
          {isCreator || attempts >= 3 || isSuccess ? (
            <Button onClick={goToNextWord} disabled={isGeneratingScore} color="light-blue">
              <ArrowRight />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default WordsPracticeReading
